import { AclVerb } from '@agilelab/plugin-wb-access-control-common';
import { AccessControlResponseNotification } from '@agilelab/plugin-wb-notification-common';
import {
  WbBadge,
  WbDrawer,
  WbDrawerContent,
  WbSearch,
  WbSelect,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { capitalize, makeStyles, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useEffect, useState } from 'react';

type ConsumableOutcome = NonNullable<
  AccessControlResponseNotification['notification_request']['consumableOutcomes']
>[number];

export interface ConsumableOutcomesDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  outcomes: ConsumableOutcome[];
  verb: AclVerb;
}

const useStyles = makeStyles(
  theme => ({
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      margin: theme.spacing(2, 0),
    },
    search: {
      flex: '1 1 240px',
    },
    searchInput: {
      minHeight: 45,
    },
    status: {
      minWidth: 160,
      flex: '1 1 160px',
    },
  }),
  { name: 'ConsumableOutcomesDrawer' },
);

export const ConsumableOutcomesDrawer = ({
  open,
  setOpen,
  outcomes,
  verb,
}: ConsumableOutcomesDrawerProps) => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);
  useEffect(() => {
    if (open) {
      setSearch('');
      setStatus('');
      setPage(0);
      setRowsPerPage(25);
      setSorting([]);
    }
  }, [open]);

  const query = search.trim().toLocaleLowerCase();
  const filteredOutcomes = outcomes
    .filter(
      item =>
        (!status || item.outcome === status) &&
        (!query ||
          (item.displayName || item.urn).toLocaleLowerCase().includes(query) ||
          item.urn.toLocaleLowerCase().includes(query)),
    )
    .map(item => ({ ...item, name: item.displayName || item.urn }));
  const activeSort = sorting[0];
  if (activeSort) {
    filteredOutcomes.sort((first, second) => {
      const firstValue =
        activeSort.id === 'outcome' ? first.outcome : first.name;
      const secondValue =
        activeSort.id === 'outcome' ? second.outcome : second.name;
      const comparison = firstValue.localeCompare(secondValue);
      return activeSort.desc ? -comparison : comparison;
    });
  }

  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(filteredOutcomes.length / rowsPerPage) - 1),
  );
  const visibleOutcomes = filteredOutcomes.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );

  return (
    <WbDrawer
      open={open}
      setOpen={setOpen}
      title={`${capitalize(verb)} access details`}
      size={50}
    >
      <WbDrawerContent>
        <Typography variant="body2" color="textSecondary">
          {outcomes.length} consumable{' '}
          {outcomes.length === 1 ? 'result' : 'results'}
        </Typography>
        <div className={classes.filters}>
          <div className={classes.search}>
            <WbSearch
              id="consumable-outcome-search"
              label="Search consumables"
              fullWidth
              InputProps={{ classes: { root: classes.searchInput } }}
              value={search}
              onChange={value => {
                setSearch(value);
                setPage(0);
              }}
            />
          </div>
          <div className={classes.status}>
            <WbSelect
              label="Status"
              fullWidth
              clearable
              value={status}
              onChange={event => {
                setStatus(event.target.value || '');
                setPage(0);
              }}
              options={['completed', 'failed']}
              getOptionLabel={v => capitalize(v)}
            />
          </div>
        </div>
        {filteredOutcomes.length ? (
          <div role="region" aria-label="Consumable request results">
            <WbTable<ConsumableOutcome & { name: string }>
              sorting={sorting}
              onSortingChange={update => {
                setSorting(update);
                setPage(0);
              }}
              manualSorting
              pagination={{
                rowsPerPageOptions: [25, 50, 100],
                count: filteredOutcomes.length,
                limit: rowsPerPage,
                currentPage,
                onPageChange: setPage,
                onRowsPerPageChange: size => {
                  setRowsPerPage(size);
                  setPage(0);
                },
              }}
              components={{
                tableContent: {
                  columns: [
                    {
                      field: 'name',
                      headerName: 'Consumable name',
                      width: '70%',
                      sortable: true,
                    },
                    {
                      field: 'outcome',
                      headerName: 'Processing status',
                      width: '30%',
                      sortable: true,
                      fieldRender: item => (
                        <WbBadge
                          variant="stroke"
                          color={
                            item.outcome === 'completed' ? 'success' : 'error'
                          }
                          startIcon={
                            item.outcome === 'completed' ? (
                              <CheckIcon fontSize="small" />
                            ) : (
                              <ErrorOutlineIcon fontSize="small" />
                            )
                          }
                        >
                          <Typography component="span" variant="caption">
                            {item.outcome === 'completed'
                              ? 'Completed'
                              : 'Failed'}
                          </Typography>
                        </WbBadge>
                      ),
                    },
                  ],
                  rows: visibleOutcomes,
                },
              }}
            />
          </div>
        ) : (
          <Typography role="status">
            No consumables match your search or status filter.
          </Typography>
        )}
      </WbDrawerContent>
    </WbDrawer>
  );
};
